import axios from 'axios';
import { refreshManager } from './refreshManager';

type RuntimeWindow = Window & {
  __APP_RUNTIME_API_URL?: string;
};

const DEFAULT_API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

const resolveRuntimeApiUrl = (): string => {
  if (typeof window === 'undefined') {
    return DEFAULT_API_URL;
  }

  const runtimeWindow = window as RuntimeWindow;

  if (runtimeWindow.__APP_RUNTIME_API_URL) {
    return runtimeWindow.__APP_RUNTIME_API_URL;
  }

  try {
    const stored = window.localStorage?.getItem('app-api-url');
    if (stored) {
      runtimeWindow.__APP_RUNTIME_API_URL = stored;
      return stored;
    }
  } catch (error) {
    console.warn('Unable to read runtime API override from storage:', error);
  }

  return DEFAULT_API_URL;
};

// Flag to prevent logout loops
let isLoggingOut = false;

// Debug: Log the environment variable (DISABLED TO STOP SPAM)
// console.log('VITE_API_URL from env:', import.meta.env.VITE_API_URL);
// console.log('All env vars:', import.meta.env);

const api = axios.create({
  baseURL: resolveRuntimeApiUrl(),
  timeout: 10000,
  withCredentials: true, // Enable sending cookies
  headers: {
    'Content-Type': 'application/json',
  },
});

if (typeof window !== 'undefined') {
  const runtimeWindow = window as RuntimeWindow;
  if (runtimeWindow.__APP_RUNTIME_API_URL && runtimeWindow.__APP_RUNTIME_API_URL !== api.defaults.baseURL) {
    api.defaults.baseURL = runtimeWindow.__APP_RUNTIME_API_URL;
  }
}

// Request interceptor - tokens now handled via HttpOnly cookies automatically
// No need to manually add Authorization header
api.interceptors.request.use(
  (config) => {
    // Cookies are automatically sent with withCredentials: true
    // No manual token handling needed
    return config;
  },
  (error) => Promise.reject(error)
);

// Refresh manager handles all token refresh logic atomically

// Event system for auth state changes to prevent circular dependencies
type AuthEvent = 'logout' | 'session-expired';
const authEventListeners: { [K in AuthEvent]: (() => void)[] } = {
  'logout': [],
  'session-expired': []
};

export const authEvents = {
  subscribe: (event: AuthEvent, callback: () => void) => {
    authEventListeners[event].push(callback);
    return () => {
      const index = authEventListeners[event].indexOf(callback);
      if (index > -1) authEventListeners[event].splice(index, 1);
    };
  },
  emit: (event: AuthEvent) => {
    authEventListeners[event].forEach(callback => {
      try {
        callback();
      } catch (error) {
        console.warn(`Auth event handler error for ${event}:`, error);
      }
    });
  }
};

// Response interceptor for token refresh with improved error handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Handle 401 errors (unauthorized) 
    if (error.response?.status === 401) {
      // Skip refresh for auth endpoints to prevent loops
      if (originalRequest.url?.includes('/auth/refresh') ||
          originalRequest.url?.includes('/auth/login') ||
          originalRequest.url?.includes('/auth/register') ||
          originalRequest.url?.includes('/auth/logout') ||
          originalRequest.url?.includes('/client/auth/')) {
        return Promise.reject(error);
      }

      // On public auth pages, don't attempt refresh — the user isn't logged in
      const path = typeof window !== 'undefined' ? window.location.pathname : '';
      if (path === '/login' || path === '/register' || path === '/forgot-password' ||
          path === '/client/login' || path === '/client/register' || path === '/client/reset-password') {
        return Promise.reject(error);
      }
      
      // Prevent infinite retry loops
      if (originalRequest._retryCount && originalRequest._retryCount >= 1) {
        // Max retries reached - session expired
        if (!isLoggingOut) {
          isLoggingOut = true;
          
          // Emit session expired event instead of direct store manipulation
          authEvents.emit('session-expired');
          
          // Reset logout flag after delay
          setTimeout(() => { isLoggingOut = false; }, 2000);
        }
        return Promise.reject(new Error('Session expired'));
      }
      
      // Increment retry count
      originalRequest._retryCount = (originalRequest._retryCount || 0) + 1;
      
      try {
        // Use atomic refresh manager to handle token refresh
        const refreshSuccess = await refreshManager.refresh(api.defaults.baseURL!);
        
        if (refreshSuccess) {
          // Retry the original request with refreshed token
          return api(originalRequest);
        } else {
          throw new Error('Token refresh failed');
        }
      } catch (refreshError: any) {
        // Token refresh failed - emit session expired event
        if (!isLoggingOut) {
          isLoggingOut = true;
          authEvents.emit('session-expired');
          setTimeout(() => { isLoggingOut = false; }, 2000);
        }
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

export default api;
